import React from 'react'
import { Box, Card, Chip } from '@mui/material'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { useTranslation } from 'react-i18next'
import WTypography from '../../../Components/whiteTypography'

/**
 * DiagnosticPanel
 * 3 colonnes Forces / Faiblesses / Risques (synthèse écrite).
 */
function DiagnosticPanel({ diagnostic }) {
  const { t } = useTranslation('layout')

  return (
    <Card
      sx={{
        background: '#0e1a36',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 3,
        p: 3,
        color: 'white',
      }}
    >
      <WTypography variant="overline" sx={{ color: '#a0b4d6 !important', letterSpacing: 1.5 }}>
        {t('conseillerPatrimonial.diagnostic.overline')}
      </WTypography>
      <WTypography variant="h5" sx={{ fontWeight: 700, mt: 0.5, mb: 3 }}>
        {t('conseillerPatrimonial.diagnostic.title')}
      </WTypography>

      <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr 1fr' }} gap={2}>
        <DiagnosticColumn
          title={t('conseillerPatrimonial.diagnostic.forces')}
          color="#22c55e"
          accent="rgba(34,197,94,0.12)"
          IconComponent={TrendingUpIcon}
          items={diagnostic.forces}
          renderItem={(f) => (
            <DiagnosticItem key={f.id} title={f.title} description={f.description} dotColor="#22c55e" />
          )}
        />
        <DiagnosticColumn
          title={t('conseillerPatrimonial.diagnostic.faiblesses')}
          color="#fbbf24"
          accent="rgba(251,191,36,0.12)"
          IconComponent={TrendingDownIcon}
          items={diagnostic.faiblesses}
          renderItem={(f) => (
            <DiagnosticItem
              key={f.id}
              title={f.title}
              description={f.description}
              dotColor={f.severity === 'high' ? '#f87171' : '#fbbf24'}
              badge={
                f.severity === 'high'
                  ? t('conseillerPatrimonial.diagnostic.criticalBadge')
                  : null
              }
            />
          )}
        />
        <DiagnosticColumn
          title={t('conseillerPatrimonial.diagnostic.risques')}
          color="#f87171"
          accent="rgba(248,113,113,0.12)"
          IconComponent={WarningAmberIcon}
          items={diagnostic.risques}
          renderItem={(r) => (
            <DiagnosticItem
              key={r.id}
              title={r.title}
              description={r.description}
              dotColor="#f87171"
              badge={t('conseillerPatrimonial.diagnostic.impactBadge', { level: r.impact })}
            />
          )}
        />
      </Box>
    </Card>
  )
}

function DiagnosticColumn({ title, color, accent, IconComponent, items, renderItem }) {
  return (
    <Box sx={{ bgcolor: accent, border: `1px solid ${color}30`, borderRadius: 2, p: 2 }}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <IconComponent sx={{ color }} />
        <WTypography variant="subtitle1" sx={{ fontWeight: 700, color: `${color} !important` }}>
          {title}
        </WTypography>
        <Chip
          label={items.length}
          size="small"
          sx={{
            ml: 'auto',
            bgcolor: 'rgba(255,255,255,0.08)',
            color: '#fff',
            fontWeight: 600,
            height: 20,
          }}
        />
      </Box>
      <Box display="flex" flexDirection="column" gap={1.5}>
        {items.map(renderItem)}
      </Box>
    </Box>
  )
}

function DiagnosticItem({ title, description, dotColor, badge }) {
  return (
    <Box
      sx={{
        p: 1.5,
        bgcolor: 'rgba(255,255,255,0.03)',
        borderRadius: 1.5,
        borderLeft: `3px solid ${dotColor}`,
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
        <WTypography variant="body2" sx={{ fontWeight: 600 }}>
          {title}
        </WTypography>
        {badge && (
          <Chip
            label={badge}
            size="small"
            sx={{
              bgcolor: `${dotColor}30`,
              color: dotColor,
              fontWeight: 600,
              height: 18,
              fontSize: 10,
            }}
          />
        )}
      </Box>
      <WTypography
        variant="caption"
        sx={{ color: '#c1d0eb !important', display: 'block', mt: 0.5, lineHeight: 1.5 }}
      >
        {description}
      </WTypography>
    </Box>
  )
}

export default DiagnosticPanel
